import { Button } from '@cube3/cubicle/src/core/atoms/Button';
import { GenericForm } from '@cube3/forms/src/forms/genericForm/GenericForm';
import { useGenericFormState } from '@cube3/forms/src/hooks/useGenericFormState';
import { Field } from '@cube3/forms/src/types/GenericFormTypes';
import { ModalNoticeUI } from '@cube3/ui/src/Modal/ModalNoticeUI';
import React, { useCallback, useMemo } from 'react';
import { useOpenFormEditor } from '../hooks/useOpenFormEditor';
import { useModalActions } from '../../Modals/modalActions';
import { objectFromValues } from '@cube3/forms/src/utils/objectToFormValues';
import { supportedMimeTypes } from '../config/supportedMimeTypes';
import { useGlobalSubmit } from '@cube3/cubicle/src/helpers/hooks/useGlobalSubmit';

interface ModalProps {
  modalContext: {
    title?: string;
    contractTypeId?: string;
    onSave?: (config) => void;
  };
}

const fields = [
  {
    id: 'display_name',
    type: 'string',
    label: 'Name',
    required: true,
    requiredLabel: '',
    fullWidth: true,
    placeholder: 'Enter name...',
    autoFocus: true
  } as Field<string, 'string'>,
  {
    id: 'for_mime_type',
    type: 'select',
    label: 'Select mime type',
    required: true,
    requiredLabel: '',
    options: supportedMimeTypes.map((k) => ({
      ...k,
      id: k.id.toString()
    }))
  } as Field<string, 'select'>
];

export const EditNewMetadataCategoryModal = (props: ModalProps) => {
  const {
    title = 'Create form',
    contractTypeId,
    onSave
  } = props.modalContext || {};

  const fieldsUI = useMemo(() => {
    if (contractTypeId) {
      return fields.slice(0, 1);
    }
    return fields;
  }, [contractTypeId]);

  const {
    fields: fieldsWithState,
    values,
    valid
  } = useGenericFormState({ fields: fieldsUI });
  const { onOpenFormEditor } = useOpenFormEditor(
    contractTypeId ? 'contract-key-group' : 'metadata-category'
  );
  const { previousModal, closeAllModals } = useModalActions();

  const onCreate = useCallback(() => {
    const value = objectFromValues(values);
    const realName = (value.display_name as string)?.trim();
    if (onSave) {
      onSave({ display_name: realName });
    } else {
      onOpenFormEditor({
        form: {
          type: contractTypeId ? 'contract-key-group' : 'metadata-category',
          display_name: realName,
          for_mime_type: value.for_mime_type?.['value'],
          metadata_fields: [],
          keys: [],
          contract_type_id: contractTypeId
        }
      })();
    }

    closeAllModals();
  }, [values, contractTypeId, onSave, onOpenFormEditor]);

  useGlobalSubmit(onCreate, !valid);

  return (
    <ModalNoticeUI
      title={title}
      onCloseEvent={previousModal}
      footerLeftComponent={
        <Button label="Cancel" onClick={previousModal} buttonStyle="ghost" />
      }
      footerRightComponent={
        <Button
          label={onSave ? 'Save' : 'Next'}
          disabled={!valid}
          onClick={onCreate}
          buttonStyle="solid"
          background="primary"
        />
      }
    >
      <GenericForm fields={fieldsWithState} direction="vertical" />
    </ModalNoticeUI>
  );
};
